import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getAccountsActiveLicenses} from 'src/openapi/admin/apis/AccountActiveLicenseApi';
import {useAppDispatch} from 'src/store/hooks';

import {selectSelectedColumns} from '../slices/account-search-result-columns.slice';
import {
    accountSearchResultLicenseActions,
    accountSearchResultLicenseDataSelector,
    accountSearchResultLicenseResultSelector,
} from '../slices/account-search-result-license.slice';
import {accountSearchResultStateSelector} from '../slices/account-search-result-state.slice';
import {checkShouldLoadColumn} from '../utils';

import type {AccountSearchResultCacheKeys} from '../typedef';
import type {LicenseSearchInfo} from 'src/openapi/admin/models/LicenseSearchInfo';


type Props = {
    accountIds: number[],
    totalCount: number,
    columnId: string,
};

export const useGetAccountSearchResultLicense = ({accountIds, totalCount, columnId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const selectedColumns = useSelector(selectSelectedColumns);

    const getAction = useCallback(() => apiDispatch(getAccountsActiveLicenses({
        requestBody: accountIds,
    })), [accountIds, apiDispatch]);

    const setLoadedData = useCallback((
        response: Record<string, LicenseSearchInfo>,
        dataCacheKeys: AccountSearchResultCacheKeys,
    ) => {
        dispatch(accountSearchResultLicenseActions.onSuccess(({
            cacheKeys: dataCacheKeys,
            totalCount,
            data: response,
        })));
    }, [totalCount, dispatch]);

    const shouldUpdateData = useMemo(
        () => checkShouldLoadColumn(columnId, selectedColumns),
        [columnId, selectedColumns],
    );

    return useCachedTable({
        dataSelector: accountSearchResultLicenseDataSelector,
        stateSelector: accountSearchResultStateSelector,
        resultSelector: accountSearchResultLicenseResultSelector,
        actions: accountSearchResultLicenseActions,
        getAction,
        onSuccessLoad: setLoadedData,
        shouldClearOnUnmount: false,
        shouldUpdateData,
    });
};
